import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const baseurl = process.env.REACT_APP_BASE_URL || "http://localhost:8000/api";

const handleError = (error) => {
    return error?.response?.data || { detail: error?.message || 'Request failed.' };
};

const getAuthHeaders = (accessToken) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }

    return headers;
};

const normalizeProfilePayload = (payload) => {
    if (!payload) return payload;
    if (payload.user) return payload.user;
    if (payload.profile) return payload.profile;
    if (payload.data?.user) return payload.data.user;
    if (payload.data?.profile) return payload.data.profile;
    return payload;
};

export const registerUser = createAsyncThunk(
    'auth/registerUser',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseurl}/accounts/register/`, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseurl}/accounts/login/`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const verifyEmailUser = createAsyncThunk(
    'auth/verifyEmailUser',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseurl}/accounts/verify-email/`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const resendVerificationUser = createAsyncThunk(
    'auth/resendVerificationUser',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseurl}/accounts/resend-verification/`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const forgotPasswordUser = createAsyncThunk(
    'auth/forgotPasswordUser',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseurl}/accounts/forgot-password/`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const resetPasswordUser = createAsyncThunk(
    'auth/resetPasswordUser',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${baseurl}/accounts/reset-password/`, payload, {
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const changePasswordUser = createAsyncThunk(
    'auth/changePasswordUser',
    async (payload, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            const token = state.auth?.access_token || localStorage.getItem('access_token');
            const response = await axios.post(`${baseurl}/accounts/change-password/`, payload, {
                headers: getAuthHeaders(token)
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const getProfile = createAsyncThunk(
    'auth/getProfile',
    async (_, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            const token = state.auth?.access_token || localStorage.getItem('access_token');

            if (!token) {
                return rejectWithValue({ detail: 'Please sign in again.' });
            }

            const response = await axios.get(`${baseurl}/accounts/profile/`, {
                headers: getAuthHeaders(token)
            });
            return normalizeProfilePayload(response.data);
        } catch (error) {
            if (error?.response?.status === 401) {
                return rejectWithValue({ detail: 'Session expired. Please sign in again.' });
            }
            return rejectWithValue(handleError(error));
        }
    }
);

export const updateProfile = createAsyncThunk(
    'auth/updateProfile',
    async (payload, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            const token = state.auth?.access_token || localStorage.getItem('access_token');
            const response = await axios.put(`${baseurl}/accounts/profile/`, payload, {
                headers: getAuthHeaders(token)
            });
            return normalizeProfilePayload(response.data);
        } catch (error) {
            return rejectWithValue(handleError(error));
        }
    }
);

export const logoutUser = createAsyncThunk(
    'auth/logoutUser',
    async (_, { getState }) => {
        // Logout always succeeds from the user's perspective - the local
        // session is cleared regardless of whether the backend call below
        // succeeds (e.g. the refresh token already expired).
        try {
            const state = getState();
            const accessToken = state.auth?.access_token || localStorage.getItem('access_token');
            const refreshToken = state.auth?.refresh_token || localStorage.getItem('refresh_token');
            if (refreshToken) {
                await axios.post(
                    `${baseurl}/accounts/logout/`,
                    { refresh: refreshToken },
                    { headers: getAuthHeaders(accessToken) }
                );
            }
        } catch (error) {
            // Ignore - the refresh token may already be expired/blacklisted.
        }
        return { detail: 'Logged out' };
    }
);
